import { DealItem } from '@/lib/graphql/generated/graphql';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

type IdentifierTemp = {
  id: string;
};

type EditingCellValue = {
  id: IdentifierTemp;
  field: keyof DealItem;
} | null;

// Private writable base atom
const _editingCellAtom = atom<EditingCellValue>(null);

// Public read-only atom (backward compat — pages/deal/page.tsx reads this)
export const editingCellState = atom<EditingCellValue>((get) =>
  get(_editingCellAtom)
);

// Per-row atom family: stores which field is being edited for a given activeVersion.id.
// null means this row is not being edited.
export const rowEditingAtomFamily = atomFamily((_rowId: string) =>
  atom<keyof DealItem | null>(null)
);

// Write-through atom: single point of truth for all editing writes.
// Updates the global atom and the per-row atom atomically so both stay in sync.
export const setEditingCellAtom = atom(
  null,
  (get, set, next: EditingCellValue) => {
    const prev = get(_editingCellAtom);
    if (!prev && !next) {
      return;
    }
    if (
      prev &&
      next &&
      prev.id.id === next.id.id &&
      prev.field === next.field
    ) {
      return;
    }
    if (prev && (!next || prev.id.id !== next.id.id)) {
      set(rowEditingAtomFamily(prev.id.id), null);
    }
    set(_editingCellAtom, next);
    if (next) {
      set(rowEditingAtomFamily(next.id.id), next.field);
    }
  }
);
